import { useEffect, useState } from "react";
import { MapPin, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function CustomersBySuburb() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get("/customer-suburbs")
      .then(({ data }) => setData(data))
      .catch(() => {
        setError(true);
        setData({ groups: [], total_with_suburb: 0, total_customers: 0 });
      });
  }, []);

  const download = async (suburb = "") => {
    setBusy(true);
    try {
      const { data: blob } = await api.get("/customer-suburbs/export", {
        params: suburb ? { suburb } : {},
        responseType: "blob",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = suburb ? `customers_${suburb}.csv` : "customers_by_suburb.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't download that list — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const groups = (data?.groups || []).filter((g) =>
    `${g.suburb} ${g.postcode}`.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="fade-up space-y-6" data-testid="customers-by-suburb-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold sm:text-4xl">Customers by Suburb</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Where your customers are, for post-outs and birthday mail — captured automatically
            from saved addresses.
          </p>
        </div>
        <Button
          onClick={() => download()}
          disabled={busy}
          className="h-11 rounded-none bg-[#C5A059] px-6 font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
          data-testid="suburb-download-all"
        >
          <Download className="mr-2 h-4 w-4" /> Download full list
        </Button>
      </div>

      {data && (
        <p className="text-sm text-muted-foreground" data-testid="suburb-coverage">
          {data.total_with_suburb} of {data.total_customers} customers have a recognised suburb.
        </p>
      )}

      <Input
        className="h-10 max-w-sm rounded-none"
        placeholder="Filter suburb or postcode"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        data-testid="suburb-filter-input"
      />

      <div className="overflow-x-auto border border-border">
        <table className="w-full min-w-[640px] text-sm" data-testid="suburb-table">
          <thead className="bg-[#F8F9FA]">
            <tr className="border-b border-border">
              <th className="px-3 py-2.5 text-left font-semibold">Suburb</th>
              <th className="px-3 py-2.5 text-left font-semibold">Postcode</th>
              <th className="px-3 py-2.5 text-left font-semibold">State</th>
              <th className="px-3 py-2.5 text-right font-semibold">Customers</th>
              <th className="px-3 py-2.5 text-right font-semibold">&nbsp;</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {groups.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-muted-foreground">
                  <MapPin className="mx-auto mb-2 h-5 w-5 text-[#C5A059]" />
                  {error
                    ? "Couldn't load suburb data — please refresh and try again."
                    : filter
                    ? "No suburbs match your filter."
                    : "No suburb data yet — it fills in as addresses are saved."}
                </td>
              </tr>
            )}
            {groups.map((g, i) => (
              <tr
                key={`${g.suburb}-${g.postcode}-${i}`}
                className="hover:bg-[#FBF8F2]"
                data-testid={`suburb-row-${i}`}
              >
                <td className="px-3 py-2 font-semibold">{g.suburb}</td>
                <td className="px-3 py-2">{g.postcode || "—"}</td>
                <td className="px-3 py-2">{g.state || "—"}</td>
                <td className="tabular px-3 py-2 text-right">{g.count}</td>
                <td className="px-3 py-2 text-right">
                  <Button
                    variant="outline"
                    className="h-8 rounded-none text-xs"
                    disabled={busy}
                    onClick={() => download(g.suburb)}
                    data-testid={`suburb-download-${i}`}
                  >
                    <Download className="mr-1 h-3.5 w-3.5" /> CSV
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
